import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DepartmentsService } from 'services/departments/departments';
import { AccountsService } from 'services/accounts/accounts';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { finalize } from 'rxjs/operators';
import { setToast } from 'redux/modules/toast/actions';
import invalidateCache from 'utils/api/invalidateCache';
import _ from 'lodash';
import { translate } from 'components/Translation';
import GridContainer from 'components/Grid/GridContainer';
import { hasAccess } from 'utils/permissions/permissions';
import {
	accountPermissionsSelector,
	departmentPermissionsSelector,
	userTypeSelector,
} from 'redux/modules/session/selectors';
import { invalidateAllCache } from 'redux/modules/api/cache/actions';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { useHistory } from 'react-router-dom';
import { SingleUser } from 'utils/api/models/users/singleUser';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleNumber } from 'utils/api/models/numbers/singleNumber';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import AllUsers from './subcomponents/AllUsers';
import DepartmentDetailsHeader from './subcomponents/DepartmentDetailsHeader';
import AllManagers from './subcomponents/AllManagers';

interface IDepartmentDetailsProps {
	match: {
		params: {
			departmentId: string;
			accountId: string;
		};
		url: string;
		location: string;
	};
}

const DepartmentDetails = ({ match }: IDepartmentDetailsProps) => {
	const [department, setDepartment] = useState<types.organizations.IExtendedDepartmentDetails | null>(null);
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [loadingDepartments, setLoadingDepartments] = useState<boolean>(true);
	const [loadingDepartmentDetails, setLoadingDepartmentDetails] = useState<boolean>(true);
	const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
	const dispatch = useDispatch();
	const history = useHistory();
	const departmentPermissions = useSelector(departmentPermissionsSelector);
	const accountPermissions = useSelector(accountPermissionsSelector);
	const loggedUserType = useSelector(userTypeSelector);
	const { departmentId, accountId } = match.params;
	const notAssigned: boolean = !departmentId;
	const loading: boolean = loadingDepartments || loadingAccounts || loadingDepartmentDetails;
	const isMounted = useRef(true);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch]);

	useEffect(() => {
		setLoadingAccounts(true);
		AccountsService.getCorpAccountsWithAdminsForCurrentUser()
			.pipe(finalize(() => isMounted.current && setLoadingAccounts(false)))
			.subscribe(({ accounts }) => isMounted.current && setAccounts(accounts));
	}, [dispatch]);

	useEffect(() => {
		setLoadingDepartments(true);
		DepartmentsService.getDepartmentsForCurrentUser()
			.pipe(finalize(() => isMounted.current && setLoadingDepartments(false)))
			.subscribe(({ departments }) => {
				if (isMounted.current) {
					const filteredDepartments = departments.filter((department) => department.accountId === accountId);
					setDepartments(filteredDepartments);
				}
			});
	}, [dispatch, accountId]);

	const fetchDepartmentsDetails = useCallback(
		(hasChanged: boolean) => {
			if (hasChanged) {
				dispatch(showLoadingIndicator());
			} else {
				setLoadingDepartmentDetails(true);
			}
			DepartmentsService.getDepartmentDetails(departmentId, accountId)
				.pipe(
					finalize(() => {
						if (isMounted.current) {
							if (hasChanged) {
								dispatch(hideLoadingIndicator());
							} else {
								setLoadingDepartmentDetails(false);
							}
						}
					})
				)
				.subscribe((departmentData) => {
					if (isMounted.current) {
						setDepartment((prevDepartmentData) => {
							const newNumbers: string[] = _.differenceBy(
								departmentData.numbers,
								prevDepartmentData?.numbers || [],
								'accountNum'
							).map((number) => number.accountNum);
							return {
								...departmentData,
								numbers: [
									...(departmentData?.numbers || []).map((number: types.organizations.INumber) => ({
										...number,
										new: hasChanged && newNumbers.indexOf(number.accountNum) >= 0,
									})),
								],
								managers: (departmentData.managers || []).map((manager: SingleUser) => ({
									...manager,
									accessible: hasAccess(
										{ accountId: manager.accountId, departmentId: manager.corpDepartmentId },
										loggedUserType,
										accountPermissions,
										departmentPermissions
									),
								})),
							};
						});
					}
				});
		},
		[dispatch, departmentId, accountId, loggedUserType, accountPermissions, departmentPermissions]
	);

	useEffect(() => {
		fetchDepartmentsDetails(false);
	}, [fetchDepartmentsDetails]);

	useEffect(() => {
		if (!loadingDepartments && !loadingDepartmentDetails && !loadingAccounts) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [dispatch, loadingDepartments, loadingDepartmentDetails, loadingAccounts]);

	const editDepartment = (name: string, uiColor: string) => {
		dispatch(showLoadingIndicator());
		DepartmentsService.updateDepartment({
			name,
			uiColor,
			departmentId,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				const updatedDepartment: SingleDepartment = response?.updateDepartmentResponse?.department;
				if (updatedDepartment) {
					setDepartment((department) => {
						if (department) {
							return {
								...department,
								departmentData: updatedDepartment,
							};
						} else {
							return null;
						}
					});
					invalidateCache(['getDepartmentsForCurrentUser', 'getDepartmentDetails'], true);
					dispatch(
						setToast({
							error: false,
							label: translate('accounts.department.updated'),
							actionType: translate('updated'),
						})
					);
				}
			});
	};

	const deleteDepartment = () => {
		dispatch(showLoadingIndicator());
		DepartmentsService.deleteDepartment(departmentId)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.deleteDepartmentResponse.result) {
					invalidateCache(['getDepartmentsForCurrentUser'], true);
					history.push(`/dashboard/organizations/${accountId}/departments`);
					dispatch(
						setToast({
							error: false,
							label: translate('accounts.department.deleted'),
							actionType: translate('deleted'),
						})
					);
				}
			});
	};

	const numbersAdded = () => {
		fetchDepartmentsDetails(true);
	};

	const invalidateCacheForManagers = () => {
		invalidateCache(
			[
				'getDepartmentsForCurrentUser',
				'getDepartmentDetails',
				'getCorpAccountDetails',
				'getCorpUserDetails',
				'getCorpAccountManagers',
				'getNumbersForAccount',
			],
			true
		);
	};

	const addManagers = (managers: SingleUser[], expiration: string) => {
		dispatch(showLoadingIndicator());
		const managersIds = managers.map((manager) => manager.userId);
		DepartmentsService.assignManagersToDepartment({
			departmentId,
			userIds: managersIds,
			permissionExpiration: expiration,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.arguments.returnValue) {
					invalidateCacheForManagers();
					const addedManagers = managers.map((manager: SingleUser) => ({
						...manager,
						permissionExpiration: expiration,
						new: true,
						accessible: hasAccess(
							{ accountId: manager.accountId, departmentId: manager.corpDepartmentId },
							loggedUserType,
							accountPermissions,
							departmentPermissions
						),
					}));
					setDepartment((department) => {
						if (department) {
							return {
								...department,
								managers: [...department.managers, ...addedManagers],
							};
						} else {
							return null;
						}
					});
				}
			});
	};

	const deleteManagers = (managersIds: string[]) => {
		dispatch(showLoadingIndicator());
		DepartmentsService.deleteManagersFromDepartment({
			departmentId,
			userIds: managersIds,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.deleteManagersFromDepartmentResponse.result) {
					invalidateCacheForManagers();
					const managers = _.filter(
						department?.managers || [],
						(manager: SingleUser) => managersIds.indexOf(manager.userId) < 0
					).map((manager) => ({ ...manager, new: false }));
					setDepartment((department) => {
						if (department) {
							return {
								...department,
								managers,
							};
						} else {
							return null;
						}
					});
				}
			});
	};

	const userAddedSuccessfully = () => {
		fetchDepartmentsDetails(true);
	};

	const deleteNumbers = (numbers: types.organizations.IExtendedSingleNumber[]) => {
		const userIds: string[] = _.chain(numbers)
			.map('userId')
			.filter(Boolean)
			.uniq()
			.value();
		const selectedNumbers: string[] = _.chain(numbers)
			.map('accountNum')
			.filter(Boolean)
			.uniq()
			.value();
		dispatch(showLoadingIndicator());
		DepartmentsService.deleteNumbersAndUsersFromDepartment({
			departmentId,
			userIds,
			numbers: selectedNumbers,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.deleteNumbersAndUsersFromDepartmentResponse.result) {
					// TODO: improve cache invalidation tool
					dispatch(invalidateAllCache());
					const updatedNumbers = _.filter(department?.numbers || [], (number: SingleNumber) => {
						// delete selected numbers
						if (selectedNumbers.indexOf(number.accountNum) >= 0) {
							return false;
						}
						// delete selected users without numbers
						if (userIds.indexOf(number.userId) >= 0 && !number.accountNum) {
							return false;
						}
						// rest should stay as it was
						return true;
					});
					setDepartment((department) => {
						if (department) {
							return {
								...department,
								numbers: updatedNumbers,
							};
						} else {
							return null;
						}
					});
					dispatch(
						setToast({
							error: false,
							label: translate('department.details.users.deleted'),
							actionType: translate('deleted'),
						})
					);
				}
			});
	};

	const expirationDateEdited = (userId: string, expirationDate: string) => {
		if (department) {
			const managers = [...department.managers];
			const index = _.findIndex(department.managers, (manager) => manager.userId === userId);
			if (index >= 0) {
				managers[index].permissionExpiration = expirationDate;
				setDepartment({
					...department,
					managers,
				});
			}
		}
	};

	return (
		<ConditionalRender
			show={!loading}
			onTrue={() => (
				<GridContainer>
					<BreadcrumbsItem to={match.url}>
						{department?.departmentData?.name || translate('department.not.assigned')}
					</BreadcrumbsItem>
					<DepartmentDetailsHeader
						notAssigned={notAssigned}
						department={department}
						editDepartment={editDepartment}
						deleteDepartment={deleteDepartment}
					/>
					<AllManagers
						notAssigned={notAssigned}
						department={department}
						accounts={accounts}
						deleteManagers={deleteManagers}
						loggedUserType={loggedUserType}
						departmentId={departmentId}
						accountId={accountId}
						addManagers={addManagers}
						userAddedSuccessfully={userAddedSuccessfully}
						departments={departments}
						expirationDateEdited={expirationDateEdited}
					/>
					<AllUsers
						department={department}
						departmentId={departmentId}
						accountId={accountId}
						departments={departments}
						numbersAdded={numbersAdded}
						deleteNumbers={deleteNumbers}
						notAssigned={notAssigned}
					/>
				</GridContainer>
			)}
		/>
	);
};

export default DepartmentDetails;
